<template>
  <div id="accordionTemplateContacts" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTemplateContacts" class="accordion-header">
        <button
          aria-controls="collapseTemplateContacts"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTemplateContacts"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="template-setup-accordion-icon me-8 d-flex align-items-center justify-content-center">
              <v-icon icon="contact" size="xs" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("template.contact.contacts") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("template.contact.contactsSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTemplateContacts"
        aria-labelledby="headingTemplateContacts"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTemplateContacts"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <form id="template-update-contact-text-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset>
              <legend class="visually-hidden">{{ $t("template.contact.contactInformation") }}</legend>

              <div class="form-outline">
                <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                  <span>{{ $t("template.contact.contactInformation") }}</span>
                </label>

                <v-html-editor
                  id="template-update-contact-text"
                  ref="template-update-contact-text-ref"
                  :value="contactInformationLocal"
                  @input="contactInformationLocal = $event"
                  @media-uploaded="editorMedia.push($event)"
                  height="300"
                />
              </div>
            </fieldset>

            <v-button
              :disabled="!meta.valid || disableButton || !meta.dirty"
              :is-loading="accepted"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="updateTemplate"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { useField } from "vee-validate";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Template from "@/api/objects/Template";
import { useToast } from "vue-toastification";

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  template: {
    type: Object as PropType<Template>,
    required: true,
  },
});

const emit = defineEmits(["reloadTemplate"]);
const { t } = useI18n();
const toast = useToast();

const rules = yup.object({
  contactInformationLocal: yup.string().nullable().label(t("template.contact.contactInformation")),
});
const initialValues = {
  contactInformationLocal: props.template.contactText,
};
const form = useFormValidation(rules, initialValues);
const { value: contactInformationLocal } = useField<string>("contactInformationLocal");
const { meta, accepted, toggleAccepted } = form;

const disableButton = ref(false);
const editorMedia = ref([]);

const updateTemplate = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      name: props.template.name,
      travel_type: props.template.travelType,
      origin: props.template.originId,
      destination: props.template.destinationId,
      is_published: props.template.isPublished,
      terms_conditions_text: props.template.termsConditionsText,
      complementary_text: props.template.complementaryText,
      contact_text: contactInformationLocal.value,
    };

    const template = await api.activityService.updateActivity(props.agency, props.template.id, data);

    if (editorMedia.value.length > 0) {
      const mediaData = {
        media: editorMedia.value,
        model_type: "activity",
        collection: "tinymce",
        model_id: props.template.id,
      };

      await api.media.confirm("s3", mediaData);
    }

    toast.success("Saved successfully", { position: "bottom-right" });

    emit("reloadTemplate");

    disableButton.value = true;
    toggleAccepted();
  } catch (e: any) {
    disableButton.value = false;
    toggleAccepted();

    console.error(e);
    toast.error(e.response?.data?.message || "An error occurred", { position: "bottom-right" });
  }
};
</script>

<style lang="scss" scoped>
.accordion-item {
  background-color: transparent;
}

.accordion .accordion-item .accordion-button {
  font-size: 1rem;
  padding: 0.5rem 1rem !important;
}

.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.9375rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.8125rem !important;
}
</style>
